import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import { AppContext } from '../../contexts/AppContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Badge from '@mui/material/Badge';
import SendOutlined from '@mui/icons-material/SendOutlined';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import useTheme from '@mui/material/styles/useTheme';
import dayjs from 'dayjs';
import InviteReviewerDialog from './InviteReviewerDialog';
import { ReviewInvite } from '../../services/ReviewService';

interface ReviewBoxProps {
  planId: string;
  planOwnerId: string;
  currentSection: number;
  currentUserId: string;
  isOwner: boolean;
}

const ReviewBox: React.FC<ReviewBoxProps> = observer(({ planId, planOwnerId, currentSection, currentUserId, isOwner }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { reviewService } = React.useContext(AppContext);

  const [messageText, setMessageText] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);
  const [selectedReviewerUserId, setSelectedReviewerUserId] = React.useState<string | null>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // For reviewer, the reviewerUserId is always themselves
  const activeReviewerUserId = isOwner ? selectedReviewerUserId : currentUserId;

  React.useEffect(() => {
    reviewService.subscribeToThreadsForPlan(planId, planOwnerId, currentUserId);
    if (isOwner) {
      reviewService.loadInvitesForPlan(planId, planOwnerId);
    }
    return () => {
      reviewService.unsubscribeFromThreads();
    };
  }, [planId, planOwnerId, currentUserId, isOwner, reviewService]);

  // Auto-select first accepted reviewer for owner
  React.useEffect(() => {
    if (isOwner && !selectedReviewerUserId) {
      const firstAccepted = reviewService.invites.find(i => i.status === 'accepted' && i.reviewerUserId);
      if (firstAccepted?.reviewerUserId) {
        setSelectedReviewerUserId(firstAccepted.reviewerUserId);
      }
    }
  }, [reviewService.invites, isOwner, selectedReviewerUserId]);

  // Mark thread as read when viewing
  React.useEffect(() => {
    if (activeReviewerUserId) {
      reviewService.markThreadRead(planId, planOwnerId, activeReviewerUserId, currentSection, currentUserId);
    }
  }, [activeReviewerUserId, currentSection, planId, planOwnerId, currentUserId, reviewService]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [reviewService.threads.length]);

  const activeThread = activeReviewerUserId
    ? reviewService.getThreadForSection(planId, planOwnerId, activeReviewerUserId, currentSection)
    : undefined;

  const handleSend = async () => {
    if (!messageText.trim() || !activeReviewerUserId || isSending) return;
    const text = messageText.trim();
    setMessageText('');
    setIsSending(true);
    try {
      await reviewService.postMessage(planId, planOwnerId, activeReviewerUserId, currentSection, currentUserId, text);
    } finally {
      setIsSending(false);
    }
  };

  const acceptedReviewers = reviewService.invites.filter(
    (i): i is ReviewInvite & { reviewerUserId: string } => i.status === 'accepted' && !!i.reviewerUserId
  );

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {/* Owner: reviewer selector + invite button */}
      {isOwner && (
        <Box borderBottom={1} borderColor="divider" p={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
              {intl.formatMessage({ id: 'review.reviewers' })}
            </Typography>
            <IconButton size="small" onClick={() => setInviteDialogOpen(true)}>
              <PersonAddOutlined fontSize="small" />
            </IconButton>
          </Box>
          {acceptedReviewers.length === 0 ? (
            <Button
              size="small"
              startIcon={<PersonAddOutlined />}
              onClick={() => setInviteDialogOpen(true)}
              variant="outlined"
              fullWidth
              sx={{ textTransform: 'none' }}
            >
              {intl.formatMessage({ id: 'review.inviteReviewer' })}
            </Button>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {acceptedReviewers.map(invite => {
                const unread = reviewService.getThreadForSection(planId, planOwnerId, invite.reviewerUserId, currentSection)?.unreadCount[currentUserId] || 0;
                const isSelected = selectedReviewerUserId === invite.reviewerUserId;
                return (
                  <Badge key={invite.reviewerUserId} badgeContent={unread} color="error">
                    <Button
                      size="small"
                      variant={isSelected ? 'contained' : 'outlined'}
                      onClick={() => setSelectedReviewerUserId(invite.reviewerUserId)}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      {invite.reviewerName || invite.reviewerEmail.split('@')[0]}
                    </Button>
                  </Badge>
                );
              })}
            </Box>
          )}
        </Box>
      )}


      {/* Thread messages */}
      <Box flex={1} overflow="auto" p={1} display="flex" flexDirection="column" gap={1}>
        {!activeReviewerUserId && isOwner && (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {intl.formatMessage({ id: 'review.selectReviewer' })}
            </Typography>
          </Box>
        )}

        {activeReviewerUserId && (!activeThread || activeThread.messages.length === 0) && (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: 'review.noMessages' })}
            </Typography>
          </Box>
        )}

        {activeThread?.messages.map((msg) => {
          const isMine = msg.authorId === currentUserId;
          const senderInvite = acceptedReviewers.find(r => r.reviewerUserId === msg.authorId);
          const senderName = isMine
            ? null
            : isOwner
              ? senderInvite?.reviewerName || senderInvite?.reviewerEmail.split('@')[0] || 'Reviewer'
              : 'Owner';
          return (
            <Box
              key={`${msg.authorId}-${msg.createdAt.getTime()}`}
              display="flex"
              flexDirection={isMine ? 'row-reverse' : 'row'}
              alignItems="flex-end"
              gap={0.5}
            >
              {senderName && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-end', mb: 0.5 }}>
                  {senderName}
                </Typography>
              )}
              <Box
                maxWidth="80%"
                bgcolor={isMine ? theme.palette.primary.main : theme.palette.action.hover}
                color={isMine ? theme.palette.primary.contrastText : theme.palette.text.primary}
                borderRadius={2}
                px={1.5}
                py={0.75}
              >
                <Typography variant="body2">{msg.text}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {dayjs(msg.createdAt).format('MMM D, HH:mm')}
                </Typography>
              </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message input */}
      {activeReviewerUserId && (
        <>
          <Divider />
          <Box display="flex" alignItems="center" gap={1} p={1}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={intl.formatMessage({ id: 'review.messagePlaceholder' })}
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              multiline
              maxRows={3}
              disabled={isSending}
            />
            <IconButton onClick={handleSend} disabled={!messageText.trim() || isSending} color="primary">
              {isSending ? <CircularProgress size={20} /> : <SendOutlined />}
            </IconButton>
          </Box>
        </>
      )}

      <InviteReviewerDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        planId={planId}
        planOwnerId={planOwnerId}
      />
    </Box>
  );
});

export default ReviewBox;
